import { useState, useEffect, useRef } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import DocumentUpload from "../../components/DocumentUpload";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import { getStateFromGST } from "../../constants/gstStateCodes";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {} from // validateAgreementStartDate, // TODO: uncomment when agreement date fields are added to UI
// validateAgreementEndDate,   // TODO: uncomment when agreement date fields are added to UI
"../../utils/dateUtils";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../utils/validationUtils";
import { fetchPincodeDetailsAction } from "../../actions/pincodeAction";
// import { useDepartmentOptions } from "../../actions/fetchDepartmentListAction"; // TODO: re-enable when department dropdown is added

import { uploadOnboardingDocumentAction } from "../../actions/uploadOnboardingDocumentAction";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { getUserData } from "../../utils/authnicate";
import { fetchKYCDetailsByIdAction } from "../../actions/fetchKYCDetailsByIdAction";
import {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  ALL_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
  DOC_KEY_TO_API_TYPE,
} from "./UploadDetailsFields";
import { sendEntityKYCFormDataAction } from "../../actions/entityKYCFormDataAction";

export {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
};

// ── Entity-type required documents section ────────────────────────────────────

export const EntityDocumentsSection = ({
  entityType,
  entityFiles,
  onEntityFileChange,
  entityDocInputs = {},
  onEntityDocInputChange,
  onEntityDocInputBlur,
  existingDocuments = [],
  errors = {},
}) => {
  const docs = ENTITY_TYPE_DOCUMENTS[entityType];
  if (!docs || docs.length === 0) return null;

  const getExisting = (key) => {
    const doc = existingDocuments.find((d) => d.key === key);
    return doc?.files?.find((f) => f.isUploaded) || null;
  };

  return (
    <div className="entity-docs-section">
      <p className="entity-docs-note">
        Required documents for <strong>{entityType.replace(/_/g, " ")}</strong>
      </p>
      <div className="entity-docs-list">
        {docs.map((doc) => (
          <div key={doc.key} className="entity-doc-row">
            {doc.inputField && (
              <InputField
                name={doc.inputField.name}
                label={`${doc.label} Number`}
                type="text"
                value={entityDocInputs[doc.inputField.name] || ""}
                onChange={(e) =>
                  onEntityDocInputChange &&
                  onEntityDocInputChange(doc.inputField.name, e.target.value)
                }
                onBlur={(e) =>
                  onEntityDocInputBlur &&
                  onEntityDocInputBlur(doc.inputField.name, e.target.value)
                }
                placeholder={doc.inputField.placeholder}
                maxLength={doc.inputField.maxLength}
                required={doc.required}
                error={errors[doc.inputField.name] || ""}
              />
            )}
            <DocumentUpload
              label={`Upload ${doc.label}`}
              name={doc.key}
              file={entityFiles[doc.key] || null}
              existingFile={getExisting(doc.key)}
              onChange={(e) => onEntityFileChange(doc.key, e.target.files[0])}
              required={doc.required}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Field renderer ─────────────────────────────────────────────────────────────

export const renderFields = (
  fields,
  formData,
  errors,
  handleChange,
  handleBlur,
) =>
  fields.map((field) => {
    const show =
      !field.conditionalOn ||
      formData[field.conditionalOn] === field.conditionalValue;
    if (!show) return null;
    const value = formData[field.name] || "";
    const error = errors[field.name] || "";
    if (field.type === "select") {
      return (
        <DropDown
          key={field.name}
          name={field.name}
          value={value}
          onChange={handleChange}
          label={field.label}
          options={field.options}
          error={error}
          required={field.required}
          fullWidth
        />
      );
    }
    return (
      <InputField
        key={field.name}
        name={field.name}
        label={field.label}
        type={field.type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={field.placeholder}
        error={error}
        required={field.required}
        maxLength={field.maxLength}
        rows={field.rows}
        readOnly={field.readOnly}
      />
    );
  });

// ── Initial state ──────────────────────────────────────────────────────────────

export const initialDetailsState = {
  parentName: "",
  tradeName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  isInvoiceRequired: "",
  entityType: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  utilityType: "",
  utilityProviderName: "",
  utilityAccountNumber: "",
  utilityAddress: "",
  isMSMERegistered: "",
  msmeNumber: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  contactEmail: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  beneficiaryName: "",
  thirdPartyName: "",
  thirdPartyRelation: "",
  thirdPartyAccountNumber: "",
  thirdPartyIfscCode: "",
  thirdPartyBankName: "",
  agreementStartDate: "",
  agreementEndDate: "",
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useUploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialDetailsState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GST entries state
  const [gstEntries, setGstEntries] = useState([
    { gstNumber: "", gstState: "", gstAddress: "" },
  ]);
  const [gstFiles, setGstFiles] = useState([null]);
  const [gstErrors, setGstErrors] = useState([""]);

  // Document file states
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [entityFiles, setEntityFiles] = useState({});
  const [entityDocInputs, setEntityDocInputs] = useState({});
  const [bankFiles, setBankFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  // Existing paths from API prefill — used as fallback when user doesn't re-upload
  const [existingPaths, setExistingPaths] = useState({});

  // Stores upload API responses keyed by documentType
  // e.g. { PassbookOrCancelledChequePath: { id, fileName, filePath, documentType, extension } }
  const [uploadedDocs, setUploadedDocs] = useState({});

  const handleEntityFileChange = (key, file) =>
    setEntityFiles((prev) => ({ ...prev, [key]: file }));

  const handleEntityDocInputChange = (name, value) => {
    // Aadhar fields: digits only, max 12
    const aadharFields = [
      "doc_aadhar_number",
      "doc_aadharBoth_numbers",
      "doc_aadharTrustee_number",
      "doc_directorAadhar_number",
    ];
    if (aadharFields.includes(name)) {
      value = value.replace(/\D/g, "").slice(0, 12);
    }
    setEntityDocInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Blur validation for document number inputs
  const handleEntityDocInputBlur = (name, value) => {
    console.log(`[DocBlur] field="${name}" value="${value}"`);
    if (!value || !value.trim()) return;
    const validators = {
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (!validator) {
      console.log(`[DocBlur] No validator for "${name}" — skipping`);
      return;
    }
    const result = validator(value);
    console.log(`[DocBlur] "${name}" result:`, result);

    setErrors((prev) => {
      const e = { ...prev };
      if (result !== true) {
        e[name] = result;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panValue =
        name === "doc_pan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_pan_number"] || "").trim().toUpperCase();
      const directorPanValue =
        name === "doc_directorPan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_directorPan_number"] || "")
              .trim()
              .toUpperCase();

      if (
        (name === "doc_pan_number" || name === "doc_directorPan_number") &&
        panValue &&
        directorPanValue &&
        panValue === directorPanValue
      ) {
        e["doc_pan_number"] = "PAN Card and Director PAN must not be the same";
        e["doc_directorPan_number"] =
          "Director PAN must be different from PAN Card";
      } else {
        // Clear the cross-check errors if they no longer apply
        if (
          e["doc_pan_number"] ===
          "PAN Card and Director PAN must not be the same"
        ) {
          delete e["doc_pan_number"];
        }
        if (
          e["doc_directorPan_number"] ===
          "Director PAN must be different from PAN Card"
        ) {
          delete e["doc_directorPan_number"];
        }
      }

      return e;
    });
  };

  const handleBankFileChange = (key, file) =>
    setBankFiles((prev) => ({ ...prev, [key]: file }));

  // const departmentOptions = useDepartmentOptions(); // TODO: use when department dropdown is added
  const pincodeRef = useRef(null);

  const [prefillDone, setPrefillDone] = useState(false);
  const [beneficiaryMatchStatus, setBeneficiaryMatchStatus] = useState(null);
  // null = not checked yet | "match" | "mismatch"

  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    formData.tradeName.trim() !== "" &&
    formData.beneficiaryName.trim() !== "" &&
    formData.tradeName.trim().toLowerCase() !==
      formData.beneficiaryName.trim().toLowerCase();

  // URL params from step 1
  const entityCategory = searchParams.get("entityCategory") || "";
  // const entityLOBType = searchParams.get("entityLOBType") || "";         // TODO: show in summary
  // const lobNationality = searchParams.get("lobNationality") || "Domestic"; // TODO: include in payload when needed
  // const aaDepartmentFunction = searchParams.get("aaDepartmentFunction") || ""; // TODO: include in payload when needed
  const contactPersonEmail = searchParams.get("contactPersonEmail") || "";
  const spocEmailAddress = searchParams.get("spocEmailAddress") || "";
  const entityNameFromUrl = searchParams.get("EntityName") || "";
  const onboardingIdFromUrl =
    searchParams.get("onboardingId") ||
    searchParams.get("entityPrimaryId") ||
    "";

  console.log("Entity name : ", entityNameFromUrl);

  // ── Fetch existing KYC data by onboardingId from URL ──────────────────────
  useEffect(() => {
    // console.log("[KYC] Step 1 — URL params:", {
    //   onboardingId: searchParams.get("onboardingId"),
    //   entityPrimaryId: searchParams.get("entityPrimaryId"),
    //   entityId: searchParams.get("entityId"),
    //   EntityId: searchParams.get("EntityId"),
    //   onboardingIdFromUrl,
    // });

    if (!onboardingIdFromUrl) {
      console.log("[KYC] Step 1 — No onboardingId in URL, form stays empty");
      return;
    }
    const id = parseInt(onboardingIdFromUrl, 10);
    if (!id) {
      console.log("[KYC] Step 1 — Invalid onboardingId:", onboardingIdFromUrl);
      return;
    }

    console.log(
      "[KYC] Step 2 — Calling fetchKYCDetailsByIdAction with id:",
      id,
    );

    fetchKYCDetailsByIdAction(id).then(
      ({ success, data: record, documents }) => {
        console.log("[KYC] Step 3 — API result:", {
          success,
          hasRecord: !!record,
          docCount: documents?.length,
        });

        if (!success || !record) {
          console.log("[KYC] Step 3 — No record found, form stays empty");
          return;
        }

        console.log("[KYC] Step 4 — Record fields:", {
          entityName: record.entityName,
          tradeName: record.tradeName,
          entityType: record.entityType,
          isGSTRegistered: record.isGSTRegistered,
          isMSME: record.isRegisterdUnderMSME,
          hasBankDetails: record.provideBankDetails,
          gstListCount: record.gstList?.length,
        });

        // Store existing uploaded documents for display
        setExistingDocuments(documents || []);

        // Store existing file paths as fallback for submit (if user doesn't re-upload)
        setExistingPaths({
          AadharCardPath: record.aadharCardPath || "",
          PANCardPath: record.panCardPath || "",
          PartnershipDeedPath: record.partnershipDeedPath || "",
          LLPCertificatePath:
            record.uploadLLPCertificatePath || record.llpCertificatePath || "",
          AadharOfBothPartnersPath: record.aadharOfBothPartnersPath || "",
          CertificateOfIncorporationPath:
            record.certificateOfIncorporationPath || "",
          DirectorPANPath: record.directorPANPath || "",
          MSMECertificatePath: record.msmeCertificatePath || "",
          UtilityBillDocumentPath: record.utilityBillDocumentPath || "",
          PassbookOrCancelledChequePath:
            record.passbookOrCancelledChequePath || "",
          UploadSignedFormPath: record.uploadSignedFormPath || "",
        });
        console.log(
          "[KYC] Step 5 — Documents set in state:",
          (documents || []).map((d) => ({
            key: d.key,
            name: d.documentName,
            fileCount: d.files?.length,
            fileName: d.files?.[0]?.fileName || "—",
          })),
        );

        setFormData((prev) => ({
          ...prev,
          parentName: record.entityName || prev.parentName,
          tradeName: record.tradeName || "",
          pinCode: record.pinCode || "",
          city: record.city || "",
          State: record.state || "",
          address: record.address || "",
          natureOfBusiness: record.natureOfBusiness || "",
          isInvoiceRequired:
            record.invoiceRequired === true
              ? "yes"
              : record.invoiceRequired === false
                ? "no"
                : record.isInvoiceRequired === true
                  ? "yes"
                  : record.isInvoiceRequired === false
                    ? "no"
                    : "",
          entityType: record.entityType || "",
          CompanypanNumber: record.panCardNumber || "",
          isGSTRegistered:
            record.isGSTRegistered === true
              ? "yes"
              : record.isGSTRegistered === false
                ? "no"
                : "",
          utilityType: record.utilityType || "",
          utilityProviderName: record.providerName || "",
          utilityAccountNumber: record.account_ConsumerNumber || "",
          utilityAddress: record.addressOnUtilityBill || "",
          isMSMERegistered:
            record.isRegisterdUnderMSME === true
              ? "yes"
              : record.isRegisterdUnderMSME === false
                ? "no"
                : "",
          msmeNumber: record.msmeRegistrationNumber || "",
          contactPersonName: record.contactPersonName || "",
          contactPersonDesignation: record.designation || "",
          contactPersonMobile: record.mobileNumber || "",
          contactEmail:
            record.contactPersonEmail ||
            record.contactEmail ||
            record.emailAddress ||
            "",
          hasBankDetails:
            record.provideBankDetails === true
              ? "yes"
              : record.provideBankDetails === false
                ? "no"
                : "",
          bankName: record.bankName || "",
          accountNumber: record.accountNumber || "",
          ifscCode: record.ifscCode || "",
          accountType: record.accountType || "",
          branchName: record.branchName || "",
          beneficiaryName: record.beneficiaryName || "",
          relationWithBeneficiary: record.relationWithBeneficiary || "",
        }));

        // Pre-fill document number input fields
        setEntityDocInputs({
          doc_aadhar_number: record.aadharCardNumber || "",
          doc_pan_number: record.panCardNumber || "",
          doc_llpCertificate_number:
            record.llpCrtificateNumber || record.llpCertificateNumber || "",
          doc_partnershipDeed_ref: record.partnershipDeedNumber || "",
          doc_aadharBoth_numbers: record.aadharNumberOfBothPartners || "",
          doc_coi_number: record.certificateOfIncorporationNumber || "",
          doc_directorPan_number: record.directorPANNumber || "",
          doc_directorAadhar_number: record.directorAadharNumber || "",
          doc_trustDeed_ref: record.trustDeedNumber || "",
          doc_panTrust_number: record.panOfTrustNumber || "",
          doc_aadharTrustee_number: record.aadharNumberOfTrustee || "",
          doc_panTrustee_number: record.panOfTrusteeNumber || "",
        });

        // Pre-fill GST entries if available
        if (Array.isArray(record.gstList) && record.gstList.length > 0) {
          console.log(
            "[KYC] Step 6 — Pre-filling GST entries:",
            record.gstList.length,
            record.gstList,
          );
          const mappedEntries = record.gstList.map((g) => ({
            gstNumber: g.gstNumber || "",
            gstState: g.state || "",
            gstAddress: g.gstAddress || "",
          }));
          console.log("[KYC] Step 6 — Mapped GST entries:", mappedEntries);
          setGstEntries(mappedEntries);
          setGstFiles(record.gstList.map(() => null));
          setGstErrors(record.gstList.map(() => ""));

          // Store existing GST cert paths keyed by slot index
          setExistingPaths((prev) => {
            const updated = { ...prev };
            record.gstList.forEach((g, i) => {
              if (g.gstCertificatePath) {
                updated[`gst_file_${i}`] = g.gstCertificatePath;
              }
            });
            return updated;
          });
        } else {
          console.log(
            "[KYC] Step 6 — No GST entries to prefill. gstList:",
            record.gstList,
          );
        }

        console.log("[KYC] Step 7 — Form prefill complete ✓");
        setPrefillDone(true);
      },
    );
  }, [onboardingIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // GST entry handlers
  const addGstEntry = () => {
    setGstEntries((prev) => [
      ...prev,
      { gstNumber: "", gstState: "", gstAddress: "" },
    ]);
    setGstFiles((prev) => [...prev, null]);
    setGstErrors((prev) => [...prev, ""]);
  };
  const removeGstEntry = (index) => {
    setGstEntries((prev) => prev.filter((_, i) => i !== index));
    setGstFiles((prev) => prev.filter((_, i) => i !== index));
    setGstErrors((prev) => prev.filter((_, i) => i !== index));
  };
  const handleGstChange = (index, field, value) => {
    setGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          setGstErrors((errs) => errs.map((e, ei) => (ei === index ? "" : e)));
        }
        return updated;
      }),
    );
  };
  const handleGstBlur = (index) => {
    const gstNumber = gstEntries[index]?.gstNumber || "";
    if (!gstNumber) return;
    const result = validateGST(gstNumber);
    setGstErrors((prev) =>
      prev.map((e, ei) => (ei === index ? (result === true ? "" : result) : e)),
    );
  };
  const handleGstFileChange = (index, file) => {
    setGstFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  // Seed entity name from URL param — only if no API prefill has run yet
  useEffect(() => {
    if (entityNameFromUrl && !onboardingIdFromUrl) {
      setFormData((prev) => ({ ...prev, parentName: entityNameFromUrl }));
    }
  }, [entityNameFromUrl, onboardingIdFromUrl]);

  // Pincode auto-fill — skip the initial trigger right after API prefill
  // (city/State already set from record). Re-enables when user manually edits pinCode.
  useEffect(() => {
    const pin = (formData.pinCode || "").replace(/\D/g, "");
    if (pin.length !== 6 || pincodeRef.current === pin) return;
    // Right after prefill, city/State come from the API — don't overwrite them
    if (prefillDone && formData.city && formData.State) {
      pincodeRef.current = pin;
      return;
    }
    pincodeRef.current = pin;
    fetchPincodeDetailsAction(pin).then((result) => {
      if (result.success) {
        setFormData((prev) => ({
          ...prev,
          city: result.city,
          State: result.state,
        }));
        setErrors((prev) => {
          const e = { ...prev };
          delete e.pinCode;
          return e;
        });
      } else {
        setFormData((prev) => ({ ...prev, city: "", State: "" }));
        setErrors((prev) => ({
          ...prev,
          pinCode: "Invalid pin code, please enter the correct pin code.",
        }));
      }
    });
  }, [formData.pinCode, prefillDone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "CompanypanNumber",
      "ifscCode",
      "thirdPartyIfscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    const digitsOnlyFields = [
      "contactPersonMobile",
      "accountNumber",
      "thirdPartyAccountNumber",
    ];
    if (digitsOnlyFields.includes(name) && typeof newValue === "string")
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      newValue = newValue.replace(/\D/g, "").slice(0, 6);
      if (newValue.length < 6) pincodeRef.current = null;
      setPrefillDone(false); // allow pincode API call on manual edit
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
          "beneficiaryName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "tradeName" || name === "beneficiaryName") {
      setBeneficiaryMatchStatus(null);
    }

    if (type !== "checkbox" && errors[name]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;

    if (name === "beneficiaryName" || name === "tradeName") {
      const trade = (name === "tradeName" ? value : formData.tradeName)
        .trim()
        .toLowerCase();
      const beneficiary = (
        name === "beneficiaryName" ? value : formData.beneficiaryName
      )
        .trim()
        .toLowerCase();
      if (trade && beneficiary) {
        setBeneficiaryMatchStatus(trade === beneficiary ? "match" : "mismatch");
      }
      return;
    }

    const validators = {
      // Form fields
      CompanypanNumber: validatePAN,
      contactPersonMobile: validateMobile,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
      thirdPartyIfscCode: validateIFSC,
      thirdPartyAccountNumber: validateAccountNumber,
      // Document number input fields (entityDocInputs)
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) setErrors((prev) => ({ ...prev, [name]: result }));
      else
        setErrors((prev) => {
          const e = { ...prev };
          delete e[name];
          return e;
        });
    }
  };

  const validate = () => {
    const newErrors = {};
    ALL_FIELDS.forEach((field) => {
      // Skip bank fields entirely when user chose not to provide bank details
      if (
        BANK_FIELDS.some((b) => b.name === field.name) &&
        formData.hasBankDetails !== "yes"
      )
        return;

      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (field.required && (!value || value.toString().trim() === "")) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });

    if (formData.isGSTRegistered === "no") {
      UTILITY_FIELDS.forEach((field) => {
        if (field.required && !formData[field.name]?.trim())
          newErrors[field.name] = `${field.label} is required`;
      });
    }

    if (showThirdParty) {
      THIRD_PARTY_FIELDS.forEach((field) => {
        const value = formData[field.name];
        if (field.required && (!value || value.toString().trim() === "")) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (field.validation && value) {
          const result = field.validation(value);
          if (result !== true) newErrors[field.name] = result;
        }
      });
    }

    // Bank document upload — required when bank details are provided
    if (formData.hasBankDetails === "yes") {
      const hasBankDocUploaded =
        bankFiles["doc_bank_proof"] ||
        existingDocuments.some(
          (d) =>
            d.key === "doc_bank_proof" && d.files?.some((f) => f.isUploaded),
        );
      if (!hasBankDocUploaded) {
        newErrors["doc_bank_proof"] =
          "Bank Document (Passbook / Cancelled Cheque) is required";
      }
    }

    // GST certificate — required for each GST entry when GST is registered
    if (formData.isGSTRegistered === "yes") {
      gstEntries.forEach((entry, i) => {
        const hasUploaded =
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          existingDocuments.some(
            (d) =>
              d.key === `gst_file_${i}` && d.files?.some((f) => f.isUploaded),
          );
        if (!hasUploaded) {
          newErrors[`gst_certificate_${i}`] =
            `GST Certificate for entry ${i + 1} (${entry.gstNumber || "GST #" + (i + 1)}) is required`;
        }
      });
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);
    try {
      const loggedInUser = getUserData();
      const now = new Date().toISOString();
      const entityPrimaryId = parseInt(
        searchParams.get("onboardingId") ||
          searchParams.get("entityPrimaryId") ||
          "0",
        10,
      );

      // Helper: get filePath — new upload takes priority, falls back to existing path from API
      const getPath = (documentType) =>
        uploadedDocs[documentType]?.filePath ||
        existingPaths[documentType] ||
        "";

      // Build uploadedList from all stored upload responses
      const uploadedList = Object.values(uploadedDocs).map((doc) => ({
        success: doc.success ?? true,
        message: doc.message || "",
        id: doc.id ?? 0,
        fileName: doc.fileName || "",
        filePath: doc.filePath || "",
        documentType: doc.documentType || "",
        extension: doc.extension || "",
      }));

      // Build gstList — each entry gets its own cert path from indexed slot
      console.log("[Submit] uploadedDocs keys:", Object.keys(uploadedDocs));
      console.log("[Submit] uploadedDocs full:", uploadedDocs);
      console.log("[Submit] gstEntries count:", gstEntries.length);
      gstEntries.forEach((entry, i) => {
        const slot = uploadedDocs[`gst_file_${i}`];
        console.log(
          `[Submit] GST[${i}] gstNumber="${entry.gstNumber}" → slot key="gst_file_${i}" → filePath="${slot?.filePath || "MISSING"}" | full slot:`,
          slot,
        );
      });

      const gstList = gstEntries.map((entry, i) => ({
        id: i + 1,
        gstNumber: entry.gstNumber || "",
        state: entry.gstState || "",
        gstAddress: entry.gstAddress || "",
        gstCertificatePath:
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          "",
      }));

      console.log(
        "[Submit] Built gstList:",
        gstList.map((g) => ({
          id: g.id,
          gstNumber: g.gstNumber,
          gstCertificatePath: g.gstCertificatePath || "EMPTY",
        })),
      );

      const payload = {
        entityPrimaryId,
        kycId: "",
        tradeName: formData.tradeName || "",
        entityName: formData.parentName || "",
        pinCode: formData.pinCode || "",
        city: formData.city || "",
        state: formData.State || "",
        address: formData.address || "",
        natureOfBusiness: formData.natureOfBusiness || "",
        invoiceRequired:
          formData.isInvoiceRequired === "yes"
            ? true
            : formData.isInvoiceRequired === "no"
              ? false
              : null,
        contactPersonName: formData.contactPersonName || "",
        designation: formData.contactPersonDesignation || "",
        mobileNumber: formData.contactPersonMobile || "",
        contactPersonEmail: formData.contactEmail || "",
        entityType: formData.entityType || "",
        isRegisteredUnderMSME: convertToBoolean(formData.isMSMERegistered),
        msmeRegistrationNumber:
          formData.isMSMERegistered === "yes" ? formData.msmeNumber || "" : "",
        panCardNumber: entityDocInputs["doc_pan_number"] || "",
        aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
        llpCrtificateNumber: entityDocInputs["doc_llpCertificate_number"] || "",
        partnershipDeedNumber: entityDocInputs["doc_partnershipDeed_ref"] || "",
        aadharNumberOfBothPartners:
          entityDocInputs["doc_aadharBoth_numbers"] || "",
        certificateOfIncorporationNumber:
          entityDocInputs["doc_coi_number"] || "",
        directorPANNumber: entityDocInputs["doc_directorPan_number"] || "",
        directorAadharNumber:
          entityDocInputs["doc_directorAadhar_number"] || "",
        trustDeedNumber: entityDocInputs["doc_trustDeed_ref"] || "",
        panOfTrustNumber: entityDocInputs["doc_panTrust_number"] || "",
        aadharNumberOfTrustee:
          entityDocInputs["doc_aadharTrustee_number"] || "",
        panOfTrusteeNumber: entityDocInputs["doc_panTrustee_number"] || "",
        isGSTRegistered: convertToBoolean(formData.isGSTRegistered),
        utilityType: formData.utilityType || "",
        providerName: formData.utilityProviderName || "",
        accountOrConsumerNumber: formData.utilityAccountNumber || "",
        addressOnUtilityBill: formData.utilityAddress || "",
        provideBankDetails: formData.hasBankDetails === "yes",
        bankName: formData.bankName || "",
        accountNumber: formData.accountNumber || "",
        ifscCode: formData.ifscCode || "",
        accountType: formData.accountType || "",
        branchName: formData.branchName || "",
        beneficiaryName: formData.beneficiaryName || "",
        relationWithBeneficiary: formData.thirdPartyRelation || "",
        // Document paths — exact API field names
        msmeCertificatePath: getPath("MSMECertificatePath"),
        aadharCardPath: getPath("AadharCardPath"),
        panCardPath: getPath("PANCardPath"),
        aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath"),
        certificateOfIncorporationPath: getPath(
          "CertificateOfIncorporationPath",
        ),
        directorPANPath: getPath("DirectorPANPath"),
        utilityBillDocumentPath: getPath("UtilityBillDocumentPath"),
        passbookOrCancelledChequePath: getPath("PassbookOrCancelledChequePath"),
        uploadSignedFormPath: getPath("UploadSignedFormPath"),
        partnershipDeedPath: getPath("PartnershipDeedPath"),
        uploadLLPCertificatePath: getPath("LLPCertificatePath"),
        gstList: formData.isGSTRegistered === "yes" ? gstList : [],
        uploadedList,
        createdBy: loggedInUser?.userName || "",
        createdOn: now,
        modifiedBy: loggedInUser?.userName || "",
        modifiedOn: now,
        isDocumentUploaded: uploadedList.length > 0,
      };

      console.log("[Submit] Payload:", payload);

      const result = await sendEntityKYCFormDataAction(payload);
      if (result?.success) {
        toast.success("KYC submitted successfully!");
        // Navigate to thank-you page with entity info
        const entityName =
          formData.parentName || searchParams.get("EntityName") || "";
        const entityId =
          searchParams.get("entityId") || searchParams.get("EntityId") || "";
        navigate(
          `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}`,
        );
      } else {
        toast.error(result?.error || "Failed to submit KYC");
      }
    } catch (err) {
      toast.error("An error occurred while submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Document upload handler ────────────────────────────────────────────────
  const handleDocumentUpload = async (docKey, file) => {
    if (!file) return;

    // gst_file_0, gst_file_1 etc. → API type is always GSTCertificatePath
    // but we store the response keyed by the indexed slot so each entry is separate
    const isIndexedGst = /^gst_file_\d+$/.test(docKey);
    const documentType = isIndexedGst
      ? "GSTCertificatePath"
      : DOC_KEY_TO_API_TYPE[docKey] || docKey;
    // Storage key: use the full indexed key so each GST cert is stored separately
    const storageKey = isIndexedGst ? docKey : documentType;

    const entityId =
      searchParams.get("entityId") || searchParams.get("EntityId") || "";

    console.log(
      `[UploadDoc] key="${docKey}" → DocumentType="${documentType}" storageKey="${storageKey}" | entity="${entityId || "new"}" | file="${file.name}"`,
    );

    const result = await uploadOnboardingDocumentAction({
      entityId,
      id: 0,
      documentType,
      fileName: file.name.replace(/\.[^/.]+$/, ""),
      file,
    });

    if (result.success && result.data) {
      // Store by storageKey — indexed GST slots (gst_file_0, gst_file_1) stay separate
      setUploadedDocs((prev) => ({
        ...prev,
        [storageKey]: { ...result.data, documentType },
      }));
      console.log(
        `[UploadDoc] ✅ Saved response for storageKey="${storageKey}":`,
        result.data,
      );
    } else if (!result.success) {
      toast.error(`Failed to upload ${documentType}: ${result.error}`);
    }

    return result;
  };

  return {
    formData,
    errors,
    setErrors,
    isSubmitting,
    // departmentOptions, // TODO: re-enable when department dropdown is added
    handleChange,
    handleBlur,
    handleSubmit,
    handleDocumentUpload,
    existingDocuments,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    beneficiaryMatchStatus,
    entityCategory,
    // entityLOBType,   // TODO: re-enable when shown in UI
    contactPersonEmail,
    spocEmailAddress,
  };
};
